const { HjTreeMirror } = require('./hj-tree-mirror');
import { memoize } from 'lodash-es';

export const CONSTRUCTED_CSS_ID_PREFIX = 'constructed-style-';

export function getConstructedCssStyleId(numericId) {
  return CONSTRUCTED_CSS_ID_PREFIX + numericId;
}

export function getStyleElementsBy(sheets) {
  const stylesCtn = document.createDocumentFragment();

  sheets.forEach((sheet) => {
    const cssRulesText = sheet.rules.join(' ');
    const style = document.createElement('style');
    style.appendChild(document.createTextNode(cssRulesText));
    style.id = getConstructedCssStyleId(sheet.id);

    stylesCtn.appendChild(style);
  });

  return stylesCtn;
}

function TreeMirror(root) {
  this.hjTreeMirror = new HjTreeMirror();
  this.root = root;
  this.delegate = {
    createElement: this.hjTreeMirror.createElement,
    setAttribute: this.hjTreeMirror.setAttribute,
  };
  this.idMap = {};
  this.isEmpty = false;
  this.SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
}
TreeMirror.prototype.isSVGNamespacedElement = memoize(function (elem) {
  while (elem) {
    if (elem.tagName && elem.tagName.toLowerCase() === 'foreignobject') {
      return false;
    }

    if (
      (elem.tagName && elem.tagName.toLowerCase() === 'svg') ||
      elem.namespaceURI === this.SVG_NAMESPACE
    ) {
      return true;
    }

    elem = elem.parentNode;
  }

  return false;
});

TreeMirror.prototype.configurePage = function ({
  token,
  siteId,
  recordingId,
  pageVisitUrl,
  baseTag,
  allowlistedDomains,
  webResources,
  debug,
  getBlockedIFrameHTML,
}) {
  this.hjTreeMirror.setSceneDocument(this.root);
  this.hjTreeMirror.setToken(token);
  this.hjTreeMirror.setSiteId(siteId);
  this.hjTreeMirror.setRecordingId(recordingId);
  this.hjTreeMirror.setCurrentDomain(pageVisitUrl);
  this.hjTreeMirror.setPageVisitUrl(pageVisitUrl);
  this.hjTreeMirror.setBaseTag(baseTag);
  this.hjTreeMirror.setAllowlistedDomains(allowlistedDomains);
  this.hjTreeMirror.setWebResources(webResources);
  this.hjTreeMirror.setChildDebugEnabled(debug);
  this.hjTreeMirror.setBlockedIFrameHTMLGetter(getBlockedIFrameHTML);
};
TreeMirror.prototype.initialize = function (rootId, children) {
  if (!rootId) {
    this.isEmpty = true;
    return;
  }

  this.idMap[rootId] = this.root;
  for (var n = 0; n < children.length; n++) this.deserializeNode(children[n], this.root);
};

/**
 * Check if an attribute mutation is allowed to be played back.
 * Check BUG-3680.
 *
 * @param node: the node with attribute changes
 * @param attrName: the attrobute name changed
 * @return {boolean}
 */
export const hjAttributeMutationAllowed = (node, attrName) => {
  var tagName = node && node.tagName ? node.tagName.toLowerCase() : '',
    attribute = attrName ? attrName.toLowerCase() : '',
    iframeSrcChange = tagName === 'iframe' && attribute === 'src';
  return !iframeSrcChange || (iframeSrcChange && node.hasAttribute('data-hj-allow-iframe'));
};
var errorCount = 0;

function tryCatchForEach(func, context, timestamp) {
  return function () {
    try {
      return func.apply(this, arguments);
    } catch (exception) {
      errorCount += 1;

      if (errorCount === 20) {
        // eslint-disable-next-line no-console
        console.error('*** Error logging stopped for mutation errors (over threshold) ***');
      }
      if (errorCount < 20) {
        // eslint-disable-next-line no-console
        console.error(exception, {
          nodeData: arguments[0],
          mutation: arguments[2],
          context: context,
          mutationTimestamp: timestamp,
        });
      }
      if (errorCount < 5) {
        if (parent && parent.Sentry) {
          parent.Sentry.withScope(function (scope) {
            scope.setExtras({
              errorIndex: errorCount,
              nodeData: arguments[0],
              mutation: arguments[2],
              context: context,
              module: 'playback-tree-mirror',
            });

            const params = new URLSearchParams(window.document.location.search);
            scope.setTags({
              site: params.get('site'),
              recording: params.get('recording'),
              mutationTimestamp: timestamp,
            });

            parent.Sentry.captureException(exception);
          });
        }
      }
    }
  };
}

TreeMirror.prototype.applyChanged = function (removed, addedOrMoved, attributes, text, timestamp) {
  if (this.isEmpty) {
    return;
  }

  var _this = this;
  addedOrMoved.forEach(
    tryCatchForEach(
      function (data) {
        const parent = _this.deserializeNode(data.parentNode);
        // CHANGED: pass isSVG to deserialize
        const node = _this.deserializeNode(data, null, _this.isSVGNamespacedElement(parent));
        _this.deserializeNode(data.previousSibling);
        const parentNode = node.parentNode;
        if (parentNode) {
          parentNode.removeChild(node);
        }
      },
      'addedOrMoved1',
      timestamp,
    ),
  );
  removed.forEach(
    tryCatchForEach(
      function (data) {
        const node = _this.deserializeNode(data);
        const parentNode = node.parentNode;
        if (parentNode) {
          parentNode.removeChild(node);
        }
      },
      'removed',
      timestamp,
    ),
  );
  addedOrMoved.forEach(
    tryCatchForEach(
      function (data) {
        const parent = _this.deserializeNode(data.parentNode);
        // CHANGED: pass isSVG to deserialize
        const node = _this.deserializeNode(data, null, _this.isSVGNamespacedElement(parent));
        const previous = _this.deserializeNode(data.previousSibling);
        if (parent.shadowRoot && data.isInShadowRoot) {
          parent.shadowRoot.insertBefore(
            node,
            previous ? previous.nextSibling : parent.shadowRoot.firstChild,
          );
        } else {
          parent.insertBefore(node, previous ? previous.nextSibling : parent.firstChild);
        }
      },
      'addedOrMoved2',
      timestamp,
    ),
  );
  attributes.forEach(
    tryCatchForEach(
      function (data) {
        var node = _this.deserializeNode(data);

        Object.keys(data.attributes).forEach(function (attrName) {
          var newVal = data.attributes[attrName];
          if (hjAttributeMutationAllowed(node, attrName)) {
            if (newVal === null) {
              node.removeAttribute(attrName);
            } else {
              if (
                !_this.delegate ||
                !_this.delegate.setAttribute ||
                !_this.delegate.setAttribute(node, attrName, newVal)
              ) {
                node.setAttribute(attrName, newVal);
              }
            }
          }
        });
      },
      'attributes',
      timestamp,
    ),
  );
  text.forEach(
    tryCatchForEach(
      function (data) {
        var node = _this.deserializeNode(data);
        node.textContent = data.textContent;
      },
      'text',
      timestamp,
    ),
  );
  removed.forEach(
    tryCatchForEach(
      function (data) {
        delete _this.idMap[data.id];
      },
      'removed',
      timestamp,
    ),
  );
};

TreeMirror.prototype.deserializeNode = function (nodeData, parent, isSVG) {
  var _this = this;
  if (nodeData === null) return null;
  var node = this.idMap[nodeData.id];
  if (node) {
    return node;
  }
  var doc = this.root.ownerDocument;
  if (doc === null) doc = this.root;
  switch (nodeData.nodeType) {
    case Node.COMMENT_NODE:
      node = doc.createComment(nodeData.textContent);
      break;
    case Node.TEXT_NODE:
      node = doc.createTextNode(nodeData.textContent);
      break;
    case Node.CDATA_SECTION_NODE:
      node = doc.createTextNode(nodeData.textContent);
      break;
    case Node.DOCUMENT_TYPE_NODE:
      node = doc.implementation.createDocumentType(
        nodeData.name || 'html',
        nodeData.publicId,
        nodeData.systemId,
      );
      break;
    case Node.ELEMENT_NODE:
      // eslint-disable-next-line no-case-declarations,no-inner-declarations
      function treatAsSvg() {
        // Some tags, even though being part of an svg element, should not be treated as svg and created
        // normally instead. Currently only known one is <style>, but more found can be added here.
        var ignoredTags = ['style'];
        if (ignoredTags.indexOf(nodeData.tagName.toLowerCase()) >= 0) {
          return false;
        }
        return isSVG || nodeData.tagName === 'svg' || _this.isSVGNamespacedElement(parent);
      }

      // CHANGED: Added custom logic to add svg elements with the correct name space. [ACT-499]
      // NOTE: The entire truthy conditional branch of the if statement is legacy,
      // and only left because recordings made until 2019-10-20 don't have namespaceURI sent.
      // If you are reading this after 2020-10-20, PLEASE consider removing this code as it is never used.
      // (all recordings will be expired, unless we've changed retention policy).
      // If a node has a namespaceURI, then we handle that in the playback-script.js.
      // EXPLANATION: When tree mirror is rendering elements order is not important.
      // It basically caches elements, adds children and maybe adds props later.
      // The original tree mirror never supported SVG elements until we added code to support them.
      // The problem is that for SVG elements to work properly they have to be created with a namespace,
      // using createElementNS.
      // This logic depended on the hierarchy of elements, however sometimes when element g was added
      // it had yet to have its parent, thus normal createElement was used which caused the SVG not to render.
      // e.g. You could have a first call with { parent: {id:1}, ...<g>Element} } and then { parent:
      // svgParent, id: 1 }, which would mean that the child was created without the namespace.
      // The fix was simply storing namespaceURI when checking for mutations in the tracking script.
      if (!(nodeData && nodeData.namespaceURI) && treatAsSvg()) {
        node = doc.createElementNS(this.SVG_NAMESPACE, nodeData.tagName);
        if (nodeData.attributes.xmlns) {
          delete nodeData.attributes.xmlns;
        }
      } else {
        // /end legacy code here.
        if (this.delegate && this.delegate.createElement)
          node = this.delegate.createElement(nodeData);
        if (!node) node = doc.createElement(nodeData.tagName);
      }

      // No need to set attributes in ignored nodes
      if (node.tagName.toLowerCase().startsWith('ignore')) {
        break;
      }

      Object.keys(nodeData.attributes).forEach(function (name) {
        if (
          !_this.delegate ||
          !_this.delegate.setAttribute ||
          !_this.delegate.setAttribute(node, name, nodeData.attributes[name])
        ) {
          node.setAttribute(name, nodeData.attributes[name]);
        }
      });

      break;
    case Node.PROCESSING_INSTRUCTION_NODE:
      node = doc.createTextNode('');
  }
  if (!node) throw Error('Problem detected playing back recording.');
  this.idMap[nodeData.id] = node;
  if (parent && parent.shadowRoot && nodeData.isInShadowRoot) {
    parent.shadowRoot.appendChild(node);
  } else if (parent) {
    parent.appendChild(node);
  }

  if (nodeData && nodeData.hasShadowRoot && !nodeData.isSyntheticShadow) {
    const shadow = node.attachShadow({ mode: 'open' });

    if (nodeData.adoptedStyleSheetsRules) {
      // We create a style element for each stylesheets. Every style has an id set by CS.
      // With this configuration, we'll be easier to mirror changes in the adopted style sheet.
      /* e.g.
                - insertRule/deleteRule on a specific stylesheet
                - adding/removing sheets from the adoptedStyleSheets
                - change sheets order within adoptedStyleSheets
            */
      const styles = getStyleElementsBy(nodeData.adoptedStyleSheetsRules);
      shadow.append(styles);
    }
  }

  // If a node is ignored we don't need to deserialize and add childNodes.
  var isNodeIgnored = node && (node.tagName || '').toLowerCase().indexOf('ignore-') === 0;

  if (nodeData.childNodes && !isNodeIgnored) {
    for (var s = 0; s < nodeData.childNodes.length; s++)
      this.deserializeNode(nodeData.childNodes[s], node);
  }

  return node;
};

TreeMirror.prototype.getNodeById = function (nodeId) {
  return this.idMap[nodeId];
};

export { TreeMirror };
